"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports._formatPropertyDataForEzlynxXml = exports._generateReplacementCost = exports._generateRatingInfo = exports.determineYesOrNo = void 0;
/* eslint-disable @typescript-eslint/unbound-method */
const lodash_1 = __importDefault(require("lodash"));
const db_prop_fact_to_ezlynx_json_prop_fact_utils_1 = require("./db-prop-fact-to-ezlynx-json-prop-fact-utils");
/**
 * @private
 * @description Generates a string that represents the address in a format that ezlynx can parse.
 * @param address Address record from the quote service database.
 * @returns String that represents the address in a format that ezlynx can parse.
 * @example "123 Main St. Anytown, NY 12345"
 */
function _generateParsedAddress(address) {
    return `${address.address_1} ${address.city} ${address.state_code} ${address.postal_code}`;
}
/**
 * @description Used for ezLynx BooleanType, which requires a "Yes" or "No" value.
 * @param value value from property_facts table to convert.
 * @returns string of "Yes" or "No".
 */
function determineYesOrNo(value) {
    if (value === true)
        return "Yes";
    else
        return "No";
}
exports.determineYesOrNo = determineYesOrNo;
/**
 * @private
 * @description Generates an object that contains all of the rating info for the property data.
 * @param propertyFact Property fact record from the quote service database.
 * @returns Object that contains all of the rating info for the property data.
 */
function _generateRatingInfo(propertyFact) {
    const Dwelling = {
        1: "One Family",
        2: "Two Family",
        3: "Three Family",
        4: "Four Family",
    }[propertyFact.num_families_in_dwelling ?? Number.MAX_SAFE_INTEGER] ?? null;
    const isFromE2Value = propertyFact.property_fact_source_type_id === 4 ? true : false;
    const rawRatingInfo = {
        Construction: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapConstructionTypeToEzLynxConstructionType)(propertyFact.construction_type),
        Dwelling,
        Foundation: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapFoundationToEzlynxFoundation)(propertyFact.foundation),
        HeatingType: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapHeatingTypeToEzLynxHeatingType)(propertyFact.heating_type),
        NumberOfFullBaths: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapNumFullBathroomsToEzLynxNumFullBathrooms)(propertyFact.num_full_bathrooms),
        NumberOfHalfBaths: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapNumHalfBathroomsToEzLynxNumHalfBathrooms)(propertyFact.num_half_bathrooms),
        NumberOfStories: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapNumStoriesToEzLynxNumStories)(propertyFact.num_stories),
        ...(!isFromE2Value && {
            ProtectionClass: propertyFact.fire_protection_class,
        }),
        Roof: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapRoofCoveringTypeToEzLynxRoofType)(propertyFact.roof_type),
        RoofingUpdateYear: propertyFact.roof_update_year,
        RoofShape: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapRoofShapeToEzLynxRoofShape)(propertyFact.roof_shape),
        SquareFootage: propertyFact.square_footage,
        Structure: (0, db_prop_fact_to_ezlynx_json_prop_fact_utils_1.mapStructureTypeToEzLynxStructureType)(propertyFact.structure_type),
        YearBuilt: propertyFact.year_built,
    };
    const ratingInfoNilsRemoved = lodash_1.default.omitBy(rawRatingInfo, lodash_1.default.isNil);
    const ratingInfoValuesStringified = lodash_1.default.mapValues(ratingInfoNilsRemoved, (value) => value.toString());
    return ratingInfoValuesStringified;
}
exports._generateRatingInfo = _generateRatingInfo;
/**
 * @private
 * @description Generates an object that contains all of the replacement cost info for the property data.
 * @param propertyFact Property fact record from the quote service database.
 * @returns Object that contains all of the replacement cost info for the property data.
 */
function _generateReplacementCost(propertyFact) {
    return {
        Dwelling: Math.round(propertyFact.replacement_cost),
        // EZLynx expects an integer for replacement cost value
        ReplacementCost: Math.round(propertyFact.replacement_cost),
    };
}
exports._generateReplacementCost = _generateReplacementCost;
/**
 * @private
 * @description Wraps the property data object into a an Ezlynx client friendly JSON object for prefilling a quote request.
 * @param propertyData Property data object.
 * @returns Wrapped property data object.
 */
function _formatPropertyDataForEzlynxXml(propertyData) {
    return {
        "soap:Envelope": {
            "soap:Body": {
                HomeLookupWithReplacementCostResponse: {
                    HomeLookupWithReplacementCostResult: {
                        PropertyData: propertyData,
                    },
                },
            },
        },
    };
}
exports._formatPropertyDataForEzlynxXml = _formatPropertyDataForEzlynxXml;
const privateHelpers = {
    _generateParsedAddress,
    _generateRatingInfo,
    _generateReplacementCost,
    _formatPropertyDataForEzlynxXml,
};
exports.default = privateHelpers;
